import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { PageMeta } from '@/components/seo/PageMeta'
import { roleService, type RoleOData } from '@/lib/api/services/roles'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
// Dropdown imports removed - using direct action icons now
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/contexts/auth-context'
import { Plus, Shield, Settings, Trash2, Users, Eye, Building2, X, Loader2, ShieldAlert } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'

const allPermissions = [
  { category: 'Templates', permissions: ['template.view', 'template.create', 'template.edit', 'template.delete'] },
  { category: 'Assets', permissions: ['asset.view', 'asset.upload', 'asset.edit', 'asset.delete'] },
  { category: 'Users', permissions: ['user.view', 'user.create', 'user.edit', 'user.delete'] },
  { category: 'Analytics', permissions: ['analytics.view', 'analytics.export'] },
  { category: 'Billing', permissions: ['billing.view', 'billing.manage'] },
  { category: 'System', permissions: ['system.admin'] },
]

interface TenantOption {
  id: string
  name: string
}

export default function RolesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const [tenantName, setTenantName] = useState<string | null>(null)
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const { user: currentUser, hasPermission, hasAnyRole, isLoading: authLoading } = useAuth()

  // Check if user has admin access
  const canAccessPage = currentUser?.isSuperAdmin || hasAnyRole(['Admin', 'Tenant Admin']) || hasPermission('role.view')

  const [roles, setRoles] = useState<RoleOData[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { toast } = useToast()

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the Roles Management page. Please contact your administrator if you need access.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Load tenants for filter dropdown
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        setTenants(response.items?.map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })) || [])
      } catch {
        // Ignore error
      }
    }
    loadTenants()
  }, [])

  // Load tenant name when filtering by tenant
  useEffect(() => {
    const loadTenantName = async () => {
      if (tenantId) {
        try {
          const { tenantsService } = await import('@/lib/api/services/tenants')
          const tenant = await tenantsService.getTenant(tenantId)
          setTenantName(tenant.name)
        } catch {
          // Ignore error, just won't show tenant name
        }
      }
    }
    loadTenantName()
  }, [tenantId])

  // Clear tenant filter
  const clearTenantFilter = () => {
    searchParams.delete('tenantId')
    setSearchParams(searchParams)
    setTenantName(null)
  }

  // Set tenant filter
  const setTenantFilter = (id: string) => {
    if (id === 'all') {
      clearTenantFilter()
    } else {
      searchParams.set('tenantId', id)
      setSearchParams(searchParams)
    }
  }

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = getODataParams()

      // Add tenant filter if tenantId is provided
      if (tenantId) {
        const tenantFilter = { field: 'tenantId', operator: 'eq' as const, value: tenantId }
        params.filter = params.filter
          ? [...params.filter, tenantFilter]
          : [tenantFilter]
      }

      const response = await roleService.getRoles(params)
      setRoles(response.value || [])
      setTotalCount(response['@odata.count'] || response.value?.length || 0)
    } catch (err) {
      setError('Failed to load roles')
      toast({
        title: 'Error',
        description: 'Failed to load roles',
        variant: 'destructive',
      })
      console.error('Failed to load roles:', err)
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast, tenantId])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  const columns: ColumnDef<RoleOData>[] = [
    {
      id: 'name',
      header: 'Role',
      accessor: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (role) => (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-gray-400" />
          <span className="font-medium">{role.name}</span>
          {role.isSystemRole && (
            <Badge variant="secondary" className="text-xs">
              System
            </Badge>
          )}
          {role.isDefault && (
            <Badge variant="outline" className="text-xs">
              Default
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      accessor: 'description',
      filterable: true,
      filterType: 'text',
      cell: (role) => (
        <span className="text-gray-500 dark:text-gray-400">
          {role.description || '-'}
        </span>
      ),
    },
    {
      id: 'userCount',
      header: 'Users',
      accessor: 'userCount',
      sortable: true,
      cell: (role) => (
        <Badge variant="outline" className="gap-1">
          <Users className="h-3 w-3" />
          {role.userCount ?? 0}
        </Badge>
      ),
    },
    {
      id: 'permissions',
      header: 'Permissions',
      accessor: (role) => (role.permissions || []).length.toString(),
      cell: (role) => {
        const permissions = role.permissions || []
        return (
          <div className="flex flex-wrap gap-1">
            {permissions.slice(0, 3).map((perm) => (
              <Badge key={perm} variant="outline" className="text-xs">
                {perm}
              </Badge>
            ))}
            {permissions.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{permissions.length - 3} more
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '140px',
      cell: (role) => (
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Eye className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Users</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Settings className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit Permissions</TooltipContent>
            </Tooltip>
            {!role.isSystemRole && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Trash2 className="h-4 w-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete Role</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      ),
    },
  ]

  return (
    <>
      <PageMeta
        title="Roles | Admin"
        description="Manage user roles and permissions"
      />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Roles & Permissions</h1>
            <p className="text-gray-500 dark:text-gray-400">
              {tenantId ? `Roles for ${tenantName || 'selected company'}` : 'Manage user roles and their permissions'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Role
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Role</DialogTitle>
                <DialogDescription>
                  Define a new role with specific permissions.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Role Name</Label>
                  <Input id="name" placeholder="e.g., Content Editor" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input id="description" placeholder="Describe what this role can do" />
                </div>
                <div className="space-y-3">
                  <Label>Permissions</Label>
                  {allPermissions.map((category) => (
                    <Card key={category.category}>
                      <CardContent className="py-3 px-4">
                        <div className="space-y-2">
                          <span className="text-sm font-medium">{category.category}</span>
                          <div className="flex flex-wrap gap-x-4 gap-y-2">
                            {category.permissions.map((perm) => (
                              <div key={perm} className="flex items-center space-x-2">
                                <Checkbox id={perm} />
                                <label
                                  htmlFor={perm}
                                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {perm.split('.')[1]}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setDialogOpen(false)}>
                  Create Role
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Tenant Filter Banner */}
        {tenantId && (
          <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
            <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Showing roles for company:{' '}
                <Link
                  to={`/admin/companies/${tenantId}`}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {tenantName || tenantId}
                </Link>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearTenantFilter}
                className="h-7 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                <X className="h-4 w-4 mr-1" />
                Clear filter
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="pt-6">
            <DataTable
              columns={columns}
              data={roles}
              totalCount={totalCount}
              loading={loading}
              error={error}
              onRetry={loadRoles}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={setSort}
              filters={filters}
              onFilterChange={setFilters}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search roles..."
              toolbarActions={
                tenants.length > 0 ? (
                  <Select value={tenantId || 'all'} onValueChange={setTenantFilter}>
                    <SelectTrigger className="w-[200px] h-10">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : undefined
              }
              rowKey="id"
              emptyMessage="No roles found"
            />
          </CardContent>
        </Card>
      </div>
    </>
  )
}
